import { match, P } from "ts-pattern";

import { Enum_Componententriessmartphoneentry_Category } from "graphql.d";
import { useProductPageContext } from "shared/product/ProductPageContext/useProductPageContext";
import { Button } from "ui/Button";
import { Loader } from "ui/Loader";
import { PushableButtonLink } from "ui/PushableButtonLink";
import { StrapiImage } from "ui/StrapiImage";
import { VerticalSeparator } from "ui/VerticalSeparator";
import { formatToCurrency } from "utils/price.utils";
import { useModalState } from "utils/useModalState";
import { usePriceAdvantage } from "utils/usePriceAdvantage";
import { useI18n } from "utils/with-i18n.utils";

import { ProductOrderButton } from "./ProductOrderButton";
import { WithProductReminderModal } from "./ProductSpecifications/WithProductReminderModal";

export const StickyCommitmentDetails = () => {
  const i18n = useI18n();
  const { isOpen, closeModal, openModal } = useModalState();
  const { family, currentVariant, suppliedAccessories, subscriptionDropSkus } =
    useProductPageContext();

  const isComingSoon =
    family?.category ===
    Enum_Componententriessmartphoneentry_Category.ComingSoon;

  const isOutOfCatalogueWithReplacement =
    family?.category === Enum_Componententriessmartphoneentry_Category.Legacy &&
    family.replacement;

  const replacementModelSlug = `/smartphone${family?.replacement?.modelSlug}`;
  const modelPhoto =
    family?.replacement?.picture ?? currentVariant?.combinedPicture;
  const modelName = family?.replacement?.modelName ?? family.modelName;
  const modelColor = family?.replacement?.color ?? currentVariant?.color;
  const modelStorage = family?.replacement?.storage ?? currentVariant?.storage;
  const replacementVariant = family?.replacement?.availableCatalogueEntry;
  const originalSubscriptionPrice =
    (replacementVariant?.originalSubscriptionPrice ??
      currentVariant?.originalSubscriptionPrice) ||
    0;
  const subscriptionPrice =
    (replacementVariant?.minimumSubscriptionPrice ??
      currentVariant?.subscriptionPrice) ||
    0;
  const originalUpfrontPrice =
    (replacementVariant?.originalUpfrontPrice ??
      currentVariant?.originalUpfrontPrice) ||
    0;
  const upfrontPrice =
    (replacementVariant?.minimumUpfrontPrice ?? currentVariant?.upfrontPrice) ||
    0;
  const commitmentPeriod =
    (replacementVariant?.miniumSubscriptionCommitmentPeriod ??
      currentVariant?.commitmentPeriod) ||
    0;

  const {
    smartphoneFamilyHasSubscriptionPriceDrop,
    smartphoneFamilyHasUpfrontPriceDrop,
    hasSeniorityDiscount,
  } = usePriceAdvantage({
    originalUpfrontPrice,
    upfrontPrice,
    originalSubscriptionPrice,
    subscriptionPrice,
  });

  // If isOutOfCatalogueWithReplacement is true, we return true here
  // so that currentVariantHasSubscriptionPromotion will depend solely
  // on smartphoneFamilyHasSubscriptionPriceDrop
  const isCurrentVariantSkuInDropList = isOutOfCatalogueWithReplacement
    ? true
    : subscriptionDropSkus?.includes(currentVariant?.sku || "");

  // check if the sku of the currentVariant is part of the subscriptionDropSkus
  // i.e., currentVariant's subscription has formerSubscriptionPriceInCentsIncludingTax
  const currentVariantHasSubscriptionPromotion =
    smartphoneFamilyHasSubscriptionPriceDrop && isCurrentVariantSkuInDropList;

  const formattedOriginalPriceSubscription = formatToCurrency(
    "fr-FR",
    "EUR",
    originalSubscriptionPrice,
    undefined,
    false,
    false,
  );

  const subscriptionPriceEuros = Math.floor(subscriptionPrice / 100);
  const subscriptionPriceCents = String(subscriptionPrice % 100).padStart(
    2,
    "0",
  );
  const subscriptionPriceLabelOptions = currentVariantHasSubscriptionPromotion
    ? {
        originalPrice: formattedOriginalPriceSubscription,
        subscriptionPriceEuros,
        subscriptionPriceCents,
        originalPriceClassName:
          "text-base leading-none text-[#00000080] self-center",
        subscriptionPriceEurosClassName:
          "text-4xl font-bold leading-none items-center text-[#7D89FF]",
        subscriptionPriceCentsClassName:
          "text-lg leading-none font-bold text-[#7D89FF]",
      }
    : {
        subscriptionPriceEuros,
        subscriptionPriceCents,
        subscriptionPriceEurosClassName:
          "text-4xl font-bold leading-none items-center",
        subscriptionPriceCentsClassName: "text-lg leading-none font-bold",
      };

  const subscriptionPriceLabel = currentVariantHasSubscriptionPromotion
    ? i18n.t(
        "product.stickyCommitmentDetails.subscriptionPriceDiscount",
        subscriptionPriceLabelOptions,
        { html: true },
      )
    : i18n.t(
        "product.stickyCommitmentDetails.subscriptionPrice",
        subscriptionPriceLabelOptions,
        { html: true },
      );

  // check if the sku of the currentVariant is part of the subscriptionDropSkus
  // i.e., currentVariant's upfront has formerUpfrontPriceInCentsIncludingTax
  const currentVariantHasUpfrontPromotion =
    smartphoneFamilyHasUpfrontPriceDrop && isCurrentVariantSkuInDropList;

  const formattedOriginalPriceUpfront = formatToCurrency(
    "fr-FR",
    "EUR",
    originalUpfrontPrice,
    undefined,
    true,
    false,
  );
  const formattedPriceUpfront = hasSeniorityDiscount
    ? 0
    : formatToCurrency("fr-FR", "EUR", upfrontPrice, undefined, true, false);

  const upfrontPriceLabelOptions =
    currentVariantHasUpfrontPromotion || hasSeniorityDiscount
      ? {
          originalPrice: formattedOriginalPriceUpfront,
          upfrontPrice: formattedPriceUpfront,
          originalPriceClassName: "text-lg text-[#00000080]",
          upfrontPriceClassName: "font-bold text-lg text-[#7D89FF]",
        }
      : {
          upfrontPrice: formattedPriceUpfront,
          upfrontPriceClassName: "text-lg font-bold",
        };

  const upfrontPriceLabel =
    currentVariantHasUpfrontPromotion || hasSeniorityDiscount
      ? i18n.t(
          "product.stickyCommitmentDetails.upfrontPriceDiscount",
          upfrontPriceLabelOptions,
          { html: true },
        )
      : i18n.t(
          "product.stickyCommitmentDetails.upfrontPrice",
          upfrontPriceLabelOptions,
          { html: true },
        );

  const CTAButton = match({
    isOutOfCatalogueWithReplacement,
    isComingSoon,
  })
    .with({ isComingSoon: true }, () => (
      <Button
        className="flex-none px-12 w-full font-medium lg:text-lg 2xs:w-max"
        onClick={openModal}
      >
        {i18n.t("product.comingSoon.bookModel", {
          productName: family.modelName,
        })}
      </Button>
    ))
    .with(
      {
        isOutOfCatalogueWithReplacement: P.not(P.union(P.nullish, false)),
      },
      () => (
        <PushableButtonLink
          className="flex-none px-12 w-full font-medium lg:text-lg 2xs:w-max"
          href={replacementModelSlug}
        >
          {i18n.t("product.stickyCommitmentDetails.order", {
            productName: family?.replacement?.modelName,
          })}
        </PushableButtonLink>
      ),
    )
    .otherwise(() => (
      <ProductOrderButton className="flex-none px-12 w-full font-medium lg:text-lg 2xs:w-max" />
    ));

  const subscriptionId = currentVariant?.id;

  if (!subscriptionId) {
    return null;
  }

  return (
    <WithProductReminderModal
      isOpen={isOpen}
      closeModal={closeModal}
      productName={family.modelName!}
      subscriptionId={subscriptionId}
    >
      <div className="hidden w-screen min-w-320 md:block lg:w-480">
        <div className="flex fixed bottom-0 z-20 justify-between items-center py-4 px-20 w-full bg-white border-t border-black">
          {/* Left block */}
          <div className="flex gap-10 items-center">
            {modelPhoto ? (
              <StrapiImage
                size="thumbnail"
                image={modelPhoto}
                className="hidden min-w-[3.6rem] min-h-[4.9rem] lg:block"
                sizes="(max-width: 1024px) 50vw, 33vw"
              />
            ) : (
              <div className="m-auto">
                <Loader />
              </div>
            )}

            <div className="flex gap-6 items-center">
              <div className="hidden: flex flex-col lg:block">
                <div>
                  {i18n.t(
                    "product.stickyCommitmentDetails.deviceName",
                    { productName: modelName },
                    { html: true },
                  )}
                </div>

                <div>
                  {i18n.t(
                    "product.stickyCommitmentDetails.deviceColorAndStorage",
                    {
                      color: modelColor,
                      storage: modelStorage,
                    },
                    { html: true },
                  )}
                </div>
              </div>

              <div className="hidden sticky-lg:block">
                {i18n.t(
                  "product.stickyCommitmentDetails.plus",
                  {},
                  { html: true },
                )}
              </div>

              <div className="hidden relative mx-auto w-fit sticky-lg:block">
                <div className="absolute -top-2 left-1/2 z-10 px-4 text-sm font-bold leading-none bg-[#7D89FF] rounded-full border-2 border-[#7D89FF] shadow rotate-[-3deg] -translate-x-1/2">
                  <span className="text-xs text-white text-outline-white">
                    {i18n.t("product.stickyCommitmentDetails.freeAccessories")}
                  </span>
                </div>

                <div className="flex gap-1 justify-center items-center p-4 bg-white rounded-2xl border-2 border-[#7D89FF]">
                  {suppliedAccessories?.map(({ image, sku }) => (
                    <StrapiImage
                      key={sku}
                      size="thumbnail"
                      image={image}
                      className="min-w-[2rem] min-h-[2rem]"
                      sizes="(max-width: 1024px) 50vw, 33vw"
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Right block */}
          <div className="flex gap-6 items-center">
            {isComingSoon ? (
              <>
                {/* Availability date */}
                <div className="w-full max-w-md text-center">
                  <span className="mr-2 text-lg">
                    {i18n.t("product.comingSoon.availability")}
                  </span>
                  <br className="2xs:hidden" />
                  <span className="text-lg font-bold text-cornflower-blue">
                    {family.releaseDate}
                  </span>
                </div>
              </>
            ) : (
              <>
                <div className="flex flex-col justify-center items-end text-sm text-black">
                  <div className="flex gap-x-1 items-center">
                    {subscriptionPriceLabel}
                  </div>
                </div>

                <VerticalSeparator className="self-stretch border-[#00000030]" />

                <div>
                  <div className="flex gap-x-1 items-center">
                    {upfrontPriceLabel}
                  </div>

                  {!!commitmentPeriod
                    ? i18n.t(
                        "product.stickyCommitmentDetails.commitmentThreshold",
                        { contractTermPeriod: commitmentPeriod },
                        { html: true },
                      )
                    : i18n.t(
                        "product.stickyCommitmentDetails.withoutCommitment",
                      )}
                </div>
              </>
            )}

            {/* Order button */}
            {CTAButton}
          </div>
        </div>
      </div>
    </WithProductReminderModal>
  );
};
